import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAgent } from '~/contexts/AgentContext';
import { Actions } from '~/eds/src/components/Actions/Actions';
import { Box } from '~/eds/src/components/Box/Box';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { UserAction } from '~/eds/src/types/UserAction';

import { AGENT_IDS } from '../../chat-bot-panel/agentIds';
import { agentRegistry } from '../../chat-bot-panel/AgentRegistry';
import { useWorkflowRoutingEnabled } from '../../chat-bot-panel/hooks/useWorkflowRoutingEnabled';
import type { AgentMenuPosition } from '../hooks/useAgentMenu';

interface AgentSelectorButtonProps {
  onButtonClick: (position: AgentMenuPosition) => void;
  showMenu: boolean;
  onCloseMenu: () => void;
}

const componentStyles = {
  wrapper: {
    bottom: 1,
    position: 'absolute' as const,
    left: 1,
    paddingLeft: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    '&:hover [data-close-button]': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 'pill' as const,
    bg: 'status.active.secondary' as const,
    '& button': {
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
    },
    '&:hover': {
      bg: 'status.new' as const,
    },
  },
  closeButton: {
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    pointerEvents: 'none' as const,
    marginBottom: '-4px',
  },
};

export const AgentSelectorButton = memo<AgentSelectorButtonProps>(
  ({ onButtonClick, showMenu, onCloseMenu }) => {
    const intl = useIntl();
    const agentContext = useAgent();
    const isWorkflowRoutingEnabled = useWorkflowRoutingEnabled();

    const availableAgents = useMemo(() => {
      const allAgents = agentRegistry.getAll();
      return allAgents.filter((agent) => {
        if (agent.id === AGENT_IDS.WORKFLOW_ROUTING) {
          return isWorkflowRoutingEnabled;
        }
        return true;
      });
    }, [isWorkflowRoutingEnabled]);

    const agentSelectorAction = useMemo(() => {
      if (availableAgents.length === 0) {
        return undefined;
      }

      const hasSelectedAgent = agentContext?.selectedAgent !== null;
      const selectedAgentConfig = agentContext?.selectedAgent
        ? agentRegistry.get(agentContext.selectedAgent)
        : null;

      const handleClick = () => {
        if (showMenu) {
          onCloseMenu();
          return;
        }

        const buttonElement = document.querySelector(
          '#agent-selector-action',
        ) as HTMLElement | null;

        if (buttonElement) {
          const buttonRect = buttonElement.getBoundingClientRect();
          const menuHeight = 200;
          const spaceAbove = buttonRect.top;

          const position: AgentMenuPosition = {
            top:
              spaceAbove >= menuHeight + 8
                ? buttonRect.top - 8
                : buttonRect.bottom + 8,
            left: buttonRect.left,
          };
          onButtonClick(position);
        } else {
          onButtonClick({
            top: 100,
            left: 100,
          });
        }
      };

      const action = {
        text: hasSelectedAgent ? `@ ${selectedAgentConfig?.label || ''}` : '@',
        mode: 'chip' as const,
        level: hasSelectedAgent
          ? ('secondary-active' as const)
          : ('subtle' as const),
        tooltip: intl.formatMessage({
          id: 'Chat.selectAIAgentTooltip',
          defaultMessage: 'Select AI Agent',
          description: 'Tooltip for @ button',
        }),
        onClick: handleClick,
        id: 'agent-selector-action',
        status: hasSelectedAgent ? ('active' as const) : undefined,
        ...(hasSelectedAgent && {
          onReset: () => {
            agentContext?.selectAgent(null);
          },
        }),
      } as UserAction & { onReset?: () => void };
      return action;
    }, [
      availableAgents.length,
      agentContext?.selectedAgent,
      showMenu,
      onButtonClick,
      onCloseMenu,
      intl,
    ]);

    if (!agentSelectorAction) {
      return null;
    }

    const hasSelectedAgent = agentSelectorAction.status === 'active';
    const onReset = agentSelectorAction.onReset;

    return (
      <Box data-agent-selector-wrapper styles={componentStyles.wrapper}>
        {hasSelectedAgent ? (
          <Box data-agent-pill styles={componentStyles.pill}>
            <Actions actions={[agentSelectorAction]} size="xs" />
            {onReset && (
              <Box data-close-button styles={componentStyles.closeButton}>
                <IconButton
                  icon="x"
                  size="xs"
                  onClick={() => {
                    onReset();
                  }}
                  tooltip={intl.formatMessage({
                    id: 'Chat.resetAgentTooltip',
                    defaultMessage: 'Start new conversation',
                    description: 'Tooltip for reset agent button',
                  })}
                  PRIVATE_variant="subtle"
                  status="active"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Actions actions={[agentSelectorAction]} size="xs" />
        )}
      </Box>
    );
  },
);
